/**
 * Dashboard API Service
 *
 * Centralized service for all dashboard widget data fetching.
 * Uses ApiClient for authenticated HTTP calls with JWT token.
 *
 * Slanket til endepunktene som brukes av de 14 referanse-widgetene.
 * Nye widgets skal bygge på samme mønster: legg til fetch-funksjon her,
 * DashboardController-endepunkt i backend, og oppdater WidgetRenderer.
 *
 * @author BizClick WMS
 * @version 2.0.0
 */

import { ApiClient } from './apiClient';
import { AuthService } from './authService';
import {
  OrderSummary,
  PickingStats,
  ShippingStats,
  OperatorActivity,
  ActivityEvent,
  KPIData,
} from '../components/warehouse/modules/wms-dashboard/types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

/**
 * Get the current user's company number from JWT token
 */
function getCompanyNo(): string {
  const userData = AuthService.getUserDataFromToken();
  if (!userData?.companyNo) {
    throw new Error('Ingen brukerdata tilgjengelig');
  }
  return userData.companyNo;
}

/**
 * Build the dashboard API base URL
 */
function getDashboardBaseUrl(): string {
  return `${API_BASE_URL}/${getCompanyNo()}/dashboard`;
}

/**
 * Generic fetch helper that handles response parsing and error handling.
 * Auto-reads warehouse from localStorage if no explicit warehouseId is provided,
 * so all dashboard widgets are automatically warehouse-filtered.
 */
async function fetchDashboardData<T>(endpoint: string, params?: Record<string, string>, warehouseId?: string): Promise<T> {
  const baseUrl = getDashboardBaseUrl();
  let url = `${baseUrl}${endpoint}`;

  const effectiveWarehouseId = warehouseId ?? localStorage.getItem('bizclick_warehouse') ?? undefined;
  const searchParams = new URLSearchParams(params || {});
  if (effectiveWarehouseId) {
    searchParams.append('warehouseId', effectiveWarehouseId);
  }
  if (searchParams.toString()) {
    url += `?${searchParams.toString()}`;
  }

  const response = await ApiClient.get(url);

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error('Manglende tilgang til dashboarddata');
    }
    if (response.status === 404) {
      throw new Error('Dashboard-endepunkt ikke funnet');
    }
    throw new Error(`Kunne ikke hente dashboarddata: ${response.statusText}`);
  }

  return response.json();
}

// ========================================================
// Core endpoints used by kept widgets
// ========================================================

/** Fetch order summary data (used by animated-counter) */
export async function fetchOrderSummary(warehouseId?: string): Promise<OrderSummary> {
  return fetchDashboardData<OrderSummary>('/order-summary', undefined, warehouseId);
}

/** Fetch picking statistics (used by animated-counter) */
export async function fetchPickingStats(warehouseId?: string): Promise<PickingStats> {
  return fetchDashboardData<PickingStats>('/picking-stats', undefined, warehouseId);
}

/** Fetch shipping statistics (used by shipping-stats + animated-counter) */
export async function fetchShippingStats(warehouseId?: string): Promise<ShippingStats> {
  return fetchDashboardData<ShippingStats>('/shipping-stats', undefined, warehouseId);
}

/** Fetch operator activity/status (used by operator-status) */
export async function fetchOperatorStatus(warehouseId?: string): Promise<OperatorActivity[]> {
  return fetchDashboardData<OperatorActivity[]>('/operator-status', undefined, warehouseId);
}

/** Fetch recent activity events (used by activity-feed) */
export async function fetchActivityFeed(warehouseId?: string): Promise<ActivityEvent[]> {
  return fetchDashboardData<ActivityEvent[]>('/activity-feed', undefined, warehouseId);
}

/** Fetch KPI data (used by sparkline-kpi) */
export async function fetchKPIs(warehouseId?: string): Promise<KPIData[]> {
  return fetchDashboardData<KPIData[]>('/kpis', undefined, warehouseId);
}

/** Fetch leaderboard data (used by leaderboard) */
export async function fetchLeaderboard(warehouseId?: string): Promise<LeaderboardEntry[]> {
  return fetchDashboardData<LeaderboardEntry[]>('/leaderboard', undefined, warehouseId);
}

/** Fetch bottleneck detection data (used by bottleneck-detector) */
export async function fetchBottlenecks(warehouseId?: string): Promise<Bottleneck[]> {
  return fetchDashboardData<Bottleneck[]>('/bottlenecks', undefined, warehouseId);
}

/** Fetch zone utilization data (used by zone-utilization + mini-heatmap) */
export async function fetchZoneUtilization(warehouseId?: string): Promise<ZoneUtilization[]> {
  return fetchDashboardData<ZoneUtilization[]>('/zone-utilization', undefined, warehouseId);
}

/** Fetch order flow / funnel data (used by order-flow) */
export async function fetchOrderFlow(warehouseId?: string): Promise<OrderFlowStep[]> {
  return fetchDashboardData<OrderFlowStep[]>('/order-flow', undefined, warehouseId);
}

/** Fetch comparison data (used by comparison) */
export async function fetchComparison(warehouseId?: string): Promise<ComparisonMetric[]> {
  return fetchDashboardData<ComparisonMetric[]>('/comparison', undefined, warehouseId);
}

/** Fetch incoming deliveries (used by incoming-deliveries) */
export async function fetchIncomingDeliveries(warehouseId?: string): Promise<IncomingDelivery[]> {
  return fetchDashboardData<IncomingDelivery[]>('/incoming-deliveries', undefined, warehouseId);
}

/** Fetch remaining pick orders by delivery method (used by remaining-orders) */
export async function fetchRemainingOrders(warehouseId?: string): Promise<RemainingOrders> {
  return fetchDashboardData<RemainingOrders>('/remaining-orders', undefined, warehouseId);
}

// ========================================================
// Dashboard Targets (configurable daily goals)
// ========================================================

/** Fetch dashboard targets */
export async function fetchDashboardTargets(warehouseId?: string): Promise<DashboardTargets> {
  return fetchDashboardData<DashboardTargets>('/targets', undefined, warehouseId);
}

/** Save dashboard targets */
export async function updateDashboardTargets(targets: DashboardTargets, warehouseId?: string): Promise<void> {
  const baseUrl = getDashboardBaseUrl();
  const params = new URLSearchParams();
  if (warehouseId) params.append('warehouseId', warehouseId);
  const queryString = params.toString() ? `?${params.toString()}` : '';
  const response = await ApiClient.put(`${baseUrl}/targets${queryString}`, targets);

  if (!response.ok) {
    throw new Error(`Kunne ikke lagre målsettinger: ${response.statusText}`);
  }
}

// ========================================================
// Types
// ========================================================

export interface LeaderboardEntry {
  rank: number;
  operatorId: string;
  operatorName: string;
  linesProcessed: number;
  accuracy: number;
  avgTimePerLine: number;
  trend: 'up' | 'down' | 'stable';
}

export interface Bottleneck {
  id: string;
  type: 'zone_congestion' | 'slow_picking' | 'putaway_backlog' | 'shipping_delay';
  location: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  queueSize: number;
  avgWaitTime: number;
  recommendation: string;
}

export interface ZoneUtilization {
  zoneId: string;
  zoneName: string;
  totalBins: number;
  usedBins: number;
  utilizationPercent: number;
  activityLevel: 'cold' | 'warm' | 'hot';
  itemCount: number;
}

export interface OrderFlowStep {
  step: string;
  label: string;
  count: number;
  color: string;
}

export interface ComparisonMetric {
  name: string;
  today: number;
  yesterday: number;
  unit: string;
  changePercent: number;
  trend: 'up' | 'down' | 'stable';
}

export interface IncomingDelivery {
  id: string;
  supplierName: string;
  orderNo: string;
  lineCount: number;
  expectedTime: string;
  status: 'expected' | 'arrived' | 'receiving' | 'completed';
}

export interface RemainingByMethod {
  deliveryMethodNo: number | null;
  deliveryMethodName: string;
  notStarted: number;
  inProgress: number;
}

export interface RemainingOrders {
  totalNotStarted: number;
  totalInProgress: number;
  byDeliveryMethod: RemainingByMethod[];
}

export interface DashboardTargets {
  pickLinesDaily: number;
  receiveLinesDaily: number;
  shipmentsDaily: number;
}
